import { create } from 'zustand';
import { createComputed } from 'zustand-computed';
import { combine, devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import { deepMerge } from '../utils/misc';

export const INVITE_STATUS = Object.freeze({
  CREATED: 'created',
  STARTED: 'inprogress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  INVALID: 'invalid',
  EXPIRED: 'expired',
  LOCKED: 'locked',
});

export const FINAL_INVITE_STATUSES = [
  INVITE_STATUS.COMPLETED,
  INVITE_STATUS.CANCELLED,
  INVITE_STATUS.LOCKED,
  INVITE_STATUS.EXPIRED,
  INVITE_STATUS.INVALID,
];

function loadCookieInviteState() {
  try {
    const json =
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('soopa-invite='))
        ?.split('=')
        ?.at(1) ?? 'e30=';
    const cookie = JSON.parse(atob(decodeURIComponent(json)));
    // TODO: remove backward compatibility when api id updated
    if (cookie.id) {
      cookie.reference = cookie.id;
      delete cookie.id;
    }
    return { cookie, api: null };
  } catch (error) {
    console.error('Invite cookie is not valid', error);
    return {
      cookie: { cookie: { reference: '', status: INVITE_STATUS.INVALID }, api: null },
    };
  }
}

const computed = createComputed((state) => {
  return {
    invite: state.api ? { ...state.cookie, ...state.api } : state.cookie,
    partial: !state.api,
  };
});

export const useInviteStore = create(
  devtools(
    computed(
      combine(loadCookieInviteState(), (set, get) => ({
        setInvite: (api) => set((state) => ({ ...state, api }), true, 'INVITE/SET'),
        patchInvite: (payload) => {
          set(
            (state) => ({
              ...state,
              api: {
                ...state.api,
                onlineBookingData: deepMerge(state.api?.onlineBookingData ?? {}, payload),
              },
            }),
            true,
            'INVITE/PATCH',
          );
          return get().invite.onlineBookingData;
        },
        removeSlot: (slotId) =>
          set(
            (state) => {
              return {
                ...state,
                api: {
                  ...state.api,
                  practitioners: state.api.practitioners.map((practitioner) => {
                    const shouldRemove = (slot) => slot.slotId !== slotId;
                    const filteredSlots = practitioner.calendarSlots.filter(shouldRemove);
                    return filteredSlots.length === practitioner.calendarSlots.length
                      ? practitioner
                      : { ...practitioner, calendarSlots: filteredSlots };
                  }),
                },
              };
            },
            false,
            'INVITE/REMOVE_SLOT',
          ),
        addAttachment: (attachment) =>
          set(
            (state) => {
              return {
                ...state,
                api: {
                  ...state.api,
                  attachments: [...state.api.attachments, attachment],
                },
              };
            },
            false,
            'INVITE/ADD_ATTACHMENT',
          ),
        removeAttachment: (attachmentId) =>
          set(
            (state) => {
              return {
                ...state,
                api: {
                  ...state.api,
                  attachments: state.api.attachments.filter((x) => x.attachmentId !== attachmentId),
                },
              };
            },
            false,
            'INVITE/REMOVE_ATTACHMENT',
          ),
      })),
    ),
    { name: 'InviteStore' },
  ),
);

export function useInviteSummary() {
  return useInviteStore(
    useShallow((state) => {
      const {
        invite: { reference, status },
        partial,
      } = state;
      return { reference, status, partial };
    }),
  );
}
